import { useState } from "react";
import { motion } from "framer-motion";
import { ClipboardList, Phone, ShieldCheck, MapPin, Sparkles, Building2, Award, HeartHandshake } from "lucide-react";
import LeadCaptureForm from "../components/LeadCaptureForm";
import StickyLeadModal from "../components/StickyLeadModal";
import SmartApartmentIdx from "../components/SmartApartmentIdx";
import { useSEO } from "../lib/useSEO";
import { BRAND, SEO_KEYWORDS } from "../lib/brandConfig";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";

/**
 * Houston Apartment Search — flagship lead-gen page.
 *
 * Three variants share this same layout via the `variant` prop:
 *   - "general"  → /apartment-search   (default Houston search)
 *   - "luxury"   → /luxury-apartments  (high-end communities)
 *   - "homes"    → /homes-for-rent     (single-family + townhomes)
 *
 * Each variant gets unique SEO meta, hero copy, and lead source label.
 */

const VARIANTS = {
  general: {
    title: "Houston Apartment Search | Urban Nest Quest",
    description:
      "Search thousands of Houston apartments in real time. Filter by price, location, amenities, pet policies, and move-in specials. Free apartment locating by Urban Nest Quest.",
    path: "/apartment-search",
    keywords: SEO_KEYWORDS.apartmentSearch,
    heroEyebrow: "Houston Apartment Locator",
    heroTitle: "Houston Apartment Search",
    heroBody:
      "Search thousands of Houston apartments in real time. Filter by price, location, amenities, pet policies, and move-in specials. When you're ready, Urban Nest Quest will help you narrow down your options, schedule tours, and secure the best apartment for your needs.",
    leadSource: "apartment_search",
    bottomCtaTitle: "Need Help Choosing an Apartment?",
    bottomCtaBody:
      "Our locators know the Houston market block-by-block. Tell us what you need — we'll send your top matches with current specials.",
  },
  luxury: {
    title: "Luxury Apartments Houston | High Rise & Premium Rentals | Urban Nest Quest",
    description:
      "Discover Houston's most sought-after luxury apartments — high-rise penthouses, designer interiors, premium amenities. Free luxury apartment locating with concierge service.",
    path: "/luxury-apartments",
    keywords: SEO_KEYWORDS.luxury,
    heroEyebrow: "Luxury Apartment Locator",
    heroTitle: "Luxury Houston Apartments",
    heroBody:
      "From River Oaks high-rises to penthouse residences in Uptown Park, we connect discerning renters with Houston's most exceptional addresses. White-glove tour scheduling, exclusive specials, and a curated list — never a generic search dump.",
    leadSource: "luxury_apartments",
    bottomCtaTitle: "Concierge Service for Luxury Renters",
    bottomCtaBody:
      "Private tours, pre-negotiated specials, and a personal locator who knows every high-rise's quirks. Tell us your priorities below.",
  },
  homes: {
    title: "Houston Homes for Rent | Single-Family, Townhomes & Rental Houses | Urban Nest Quest",
    description:
      "Browse Houston single-family homes, townhomes, and rental houses. Pet-friendly, school-zone, and yard-included filters — plus free locator help from Urban Nest Quest.",
    path: "/homes-for-rent",
    keywords: SEO_KEYWORDS.homes,
    heroEyebrow: "Houston Home Rental Specialists",
    heroTitle: "Houston Homes for Rent",
    heroBody:
      "Single-family homes, townhomes, and rental houses across Greater Houston — from Katy and Sugar Land to The Heights and Memorial. Sort by school zone, yard size, garage, and pet policy.",
    leadSource: "homes_for_rent",
    bottomCtaTitle: "Looking for a Specific School Zone?",
    bottomCtaBody:
      "Tell us your zoning, yard, and pet needs — we'll send a tight shortlist of move-in-ready homes that match.",
  },
};

const TRUST_ITEMS = [
  { icon: ShieldCheck,    title: "Free Apartment Locating",     body: "Properties pay us. You pay nothing." },
  { icon: MapPin,         title: "Local Houston Expertise",     body: "Block-by-block neighborhood knowledge." },
  { icon: Sparkles,       title: "Personalized Recommendations", body: "Hand-picked matches, never spam lists." },
  { icon: Building2,      title: "Tour Scheduling",             body: "We book it — you just show up." },
  { icon: ClipboardList,  title: "Application Guidance",        body: "We help you put your best foot forward." },
  { icon: HeartHandshake, title: "Relocation Support",          body: "Out-of-towner? We've got the playbook." },
  { icon: Award,          title: "Current Specials Access",     body: "Up to 6 weeks free at participating properties." },
];

const ApartmentSearchPage = ({ variant = "general" }) => {
  const cfg = VARIANTS[variant] || VARIANTS.general;
  const [showLeadForm, setShowLeadForm] = useState(true);

  useSEO({ title: cfg.title, description: cfg.description, keywords: cfg.keywords, path: cfg.path });

  return (
    <div className="bg-[#FAFAF7] pt-20" data-testid={`apartment-search-page-${variant}`}>
      {/* Hero */}
      <section className="bg-[#1F2A44] text-white">
        <div className="container-main py-12 sm:py-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest text-[#C8A96B] border border-[#C8A96B]/40 font-semibold">
              <MapPin className="w-3.5 h-3.5" /> {cfg.heroEyebrow}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] leading-tight"
                data-testid="apartment-search-h1">
              {cfg.heroTitle}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">{cfg.heroBody}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={TYPEFORM_QUIZ_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold transition-colors"
                data-testid="hero-cta-quiz"
              >
                <ClipboardList className="w-4 h-4" /> Take the Apartment Match Quiz
              </a>
              <a
                href={BRAND.smsHref}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold transition-colors"
                data-testid="hero-cta-text"
              >
                <Phone className="w-4 h-4" /> Text us: {BRAND.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quiz CTA strip */}
      <section className="bg-gradient-to-r from-[#1F2A44] to-[#2a3556] border-t border-[#C8A96B]/30">
        <div className="container-main py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-white font-['Playfair_Display']">
              Not Sure Where to Start?
            </h2>
            <p className="text-sm text-white/70 mt-0.5 max-w-2xl">
              Take our Apartment Match Quiz and receive personalized recommendations based on your budget, lifestyle, commute, and must-have amenities.
            </p>
          </div>
          <a
            href={TYPEFORM_QUIZ_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold transition-colors"
            data-testid="quiz-strip-cta"
          >
            Take the Quiz
          </a>
        </div>
      </section>

      {/* Lead form above the iframe */}
      <section className="container-main py-10 sm:py-12">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">
              Step 1 — Tell us about you
            </p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#1F2A44] font-['Playfair_Display'] leading-tight">
              Skip the bad listings.
              <br />Get matched faster.
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#333333]/80">
              Submit your details and we'll cross-reference current Houston inventory with your budget, move date, and preferred area. You'll see the search engine below either way — but our locators will send you the best 3 matches.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-[#333333]/85">
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> 100% free service for renters</li>
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> Reply within 30 minutes (avg.)</li>
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> Access to unlisted specials & off-market units</li>
            </ul>
          </div>
          <div className="lg:col-span-3">
            <LeadCaptureForm
              source={cfg.leadSource}
              submitLabel="View Apartments"
              onSuccess={() => setShowLeadForm(true)}
            />
          </div>
        </div>
      </section>

      {/* Live IDX Inventory Search */}
      <section className="container-main pb-12">
        <SmartApartmentIdx title="Live Houston Apartment Inventory" />
      </section>

      {/* Trust Building */}
      <section className="bg-white border-y border-[#D9D9D9]">
        <div className="container-main py-12 sm:py-16">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">
              Why renters choose us
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
              Why Use Urban Nest Quest?
            </h2>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TRUST_ITEMS.map((t) => (
              <div
                key={t.title}
                className="p-5 rounded-xl border border-[#D9D9D9] bg-[#FAFAF7] hover:border-[#C8A96B] hover:bg-white transition-colors"
                data-testid={`trust-item-${t.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <t.icon className="w-6 h-6 text-[#C8A96B]" />
                <h3 className="mt-3 text-base font-bold text-[#1F2A44]">{t.title}</h3>
                <p className="mt-1 text-sm text-[#333333]/75">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1F2A44] text-white">
        <div className="container-main py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Playfair_Display']">
                {cfg.bottomCtaTitle}
              </h2>
              <p className="mt-3 text-white/80 max-w-xl">{cfg.bottomCtaBody}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={BRAND.phoneHref}
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold"
                   data-testid="bottom-cta-call">
                  <Phone className="w-4 h-4" /> Call {BRAND.phone}
                </a>
                <a href={BRAND.emailHref}
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold"
                   data-testid="bottom-cta-email">
                  Email us
                </a>
                <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-[#C8A96B] text-[#C8A96B] hover:bg-[#C8A96B] hover:text-white font-semibold"
                   data-testid="bottom-cta-quiz">
                  Take the Quiz
                </a>
              </div>
              <p className="mt-6 text-xs text-white/50">
                {BRAND.name} · {BRAND.brokerageLine}
              </p>
            </div>
            <div className="lg:pl-6">
              {showLeadForm && <LeadCaptureForm source={`${cfg.leadSource}_bottom`} submitLabel="Get My Matches" />}
            </div>
          </div>
        </div>
      </section>

      <StickyLeadModal source={`${cfg.leadSource}_sticky`} storageKey={`unq_sticky_${variant}_v1`} />
    </div>
  );
};

export default ApartmentSearchPage;
